import { useCreateFieldsetCompletion } from 'hooks/queries/workspaces/useCreateFieldsetCompletion'
import { useCallback, useState } from 'react'
import { AIJobStatus, FieldsetCompletion } from 'types'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { ChannelType } from 'components/CampaignCreationModal/Types'

interface UseGenerateTitleProps {
  onSuccess?: (title: string) => void
  onError?: () => void
}

interface TitleContextData {
  source_title?: string
  channel?: string
}

export const useGenerateTitle = ({ onSuccess, onError }: UseGenerateTitleProps = {}) => {
  const [completionId, setCompletionId] = useState<number | undefined>()
  const { mutate: createFieldsetCompletion, data } = useCreateFieldsetCompletion()

  const generateTitle = useCallback(
    (contextData: TitleContextData) => {
      createFieldsetCompletion(
        {
          fields: ['title'],
          completedFields: contextData,
          generationType: ChannelType.YoutubeScript,
        },
        {
          onSuccess: (res) => {
            if (res.fieldset_completion?.id) {
              setCompletionId(res.fieldset_completion.id)
            } else {
              showErrorMessageToast('Failed to start title generation. Please try again.')
              onError?.()
            }
          },
          onError: () => {
            showErrorMessageToast('Failed to start title generation. Please try again.')
            onError?.()
          },
        },
      )
    },
    [createFieldsetCompletion, onError],
  )

  const handleComplete = useCallback(
    (fieldsetCompletion: FieldsetCompletion) => {
      if (fieldsetCompletion.status === AIJobStatus.Successful) {
        const title = fieldsetCompletion.completed_fields.title
        if (typeof title === 'string') {
          onSuccess?.(title)
        } else {
          showErrorMessageToast('Invalid title generated. Please try again.')
          onError?.()
        }
      } else if (fieldsetCompletion.status === AIJobStatus.Failed) {
        showErrorMessageToast('Failed to generate title. Please try again.')
        onError?.()
      }
      setCompletionId(undefined)
    },
    [onSuccess, onError],
  )

  return {
    generateTitle,
    isLoading: !!completionId,
    title: data?.fieldset_completion?.completed_fields?.title as string | undefined,
    completionId,
    handleComplete,
  }
}
